import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#3B4A4F';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-2373">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-5 leading-[1.2]" data-id="element-2374">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#3A3A33] leading-relaxed" data-id="element-2375">
        {children}
      </div>
    </motion.section>;
}
export function Privacy() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2376">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#E5E5E5]" data-id="element-2377">
        <div className="max-w-3xl mx-auto" data-id="element-2378">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2379">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-2380">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.05] mb-5" data-id="element-2381">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#5A5A50]" data-id="element-2382">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-2383">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-2384">
          <motion.p variants={fadeUp} className="text-[15px] text-[#3A3A33] leading-relaxed mb-12" data-id="element-2385">
            Structured Nutrition ("we," "us," or "our") respects your privacy.
            This Privacy Policy explains what information we collect, how we use
            it, and the choices you have. By using our website and services, you
            agree to the practices described below.
          </motion.p>

          <Section title="Information We Collect" data-id="element-2386">
            <p data-id="element-2387">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2388">
              <li data-id="element-2389">
                <strong data-id="element-2390">Contact details:</strong> Name, email address, and phone
                number when you submit our contact form or sign up for an
                account.
              </li>
              <li data-id="element-2391">
                <strong data-id="element-2392">Account information:</strong> Login credentials and
                profile data managed through our authentication provider
                (Firebase).
              </li>
              <li data-id="element-2393">
                <strong data-id="element-2394">Payment information:</strong> Processed securely through
                Stripe. We do not store full credit card numbers on our servers.
              </li>
              <li data-id="element-2395">
                <strong data-id="element-2396">Communications:</strong> Messages you send us through
                forms, email, or social channels.
              </li>
              <li data-id="element-2397">
                <strong data-id="element-2398">Usage data:</strong> Pages visited, time on site, and
                device/browser information collected through cookies and
                analytics tools.
              </li>
            </ul>
          </Section>

          <Section title="How We Use Your Information" data-id="element-2399">
            <p data-id="element-2400">We use the information we collect to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2401">
              <li data-id="element-2402">Provide and improve our coaching and digital products</li>
              <li data-id="element-2403">Process payments and deliver purchases</li>
              <li data-id="element-2404">Respond to inquiries and provide customer support</li>
              <li data-id="element-2405">
                Send updates, newsletters, or marketing (only if you opt in)
              </li>
              <li data-id="element-2406">
                Comply with legal obligations and protect against fraud or abuse
              </li>
            </ul>
          </Section>

          <Section title="Third-Party Services" data-id="element-2407">
            <p data-id="element-2408">
              We share information with trusted third-party providers who help
              us operate our business:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2409">
              <li data-id="element-2410">
                <strong data-id="element-2411">Stripe</strong> — payment processing
              </li>
              <li data-id="element-2412">
                <strong data-id="element-2413">Firebase (Google)</strong> — authentication and database
              </li>
              <li data-id="element-2414">
                <strong data-id="element-2415">Formspree</strong> — contact form submissions
              </li>
              <li data-id="element-2416">
                <strong data-id="element-2417">Google Analytics / Google Tag Manager</strong> — site
                analytics
              </li>
              <li data-id="element-2418">
                <strong data-id="element-2419">Meta Pixel</strong> — advertising and analytics
              </li>
            </ul>
            <p data-id="element-2420">
              These providers have their own privacy policies governing how they
              handle your data.
            </p>
          </Section>

          <Section title="Cookies and Tracking" data-id="element-2421">
            <p data-id="element-2422">
              We use cookies and similar technologies to remember your
              preferences, keep you logged in, analyze site traffic, and measure
              advertising effectiveness. You can disable cookies in your browser
              settings, though some site features may stop working as a result.
            </p>
          </Section>

          <Section title="Your Rights" data-id="element-2423">
            <p data-id="element-2424">Depending on where you live, you may have the right to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2425">
              <li data-id="element-2426">Access the personal information we hold about you</li>
              <li data-id="element-2427">Request that we correct or delete your information</li>
              <li data-id="element-2428">Opt out of marketing communications at any time</li>
              <li data-id="element-2429">
                Withdraw consent for processing where consent is the legal basis
              </li>
            </ul>
            <p data-id="element-2430">
              To exercise any of these rights, contact us using the details
              below.
            </p>
          </Section>

          <Section title="Data Security" data-id="element-2431">
            <p data-id="element-2432">
              We use reasonable administrative, technical, and physical
              safeguards to protect your information. However, no method of
              transmission over the internet is 100% secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="Children's Privacy" data-id="element-2433">
            <p data-id="element-2434">
              Our services are not directed to individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              you believe a child has provided us information, please contact us
              so we can delete it.
            </p>
          </Section>

          <Section title="Changes to This Policy" data-id="element-2435">
            <p data-id="element-2436">
              We may update this Privacy Policy from time to time. When we do,
              we'll revise the "Last updated" date at the top of this page.
              Significant changes will be communicated via the site or email.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-2437">
            <p data-id="element-2438">
              Questions about this Privacy Policy or your data? Reach out
              through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-2439">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#5A5A50] italic mt-16 pt-8 border-t border-[#E5E5E5]" data-id="element-2440">
            This document is provided as a general template and does not
            constitute legal advice. We recommend reviewing it with an attorney
            before relying on it.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}